import { useMemo, type CSSProperties } from 'react';
import { PS } from '../lib/theme';
import { minutesToHm, formatClock } from '../lib/time';

interface Props {
  value: string;                          // "HH:MM"
  onChange: (next: string) => void;
  /** "HH:MM" — inclusive. Default 09:00. */
  min?: string;
  /** "HH:MM" — inclusive. Default 24:00 (i.e. 23:59 visible). */
  max?: string;
  /** Step in minutes. Default 15. */
  stepMinutes?: number;
  /** When true, options include the max-hour boundary (canonical value "24:00";
   *  displayed as "24:00" or, on a 12-hour device, the locale's midnight). */
  includeMax?: boolean;
  /** Placeholder text shown when value is empty. Default "—". */
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}

function parseHm(s: string): number {
  if (!s) return 0;
  const [h, m] = s.split(':').map(Number);
  return h * 60 + (m || 0);
}

/**
 * Styled <select> rendering only valid times under the policy. Use this in
 * place of <input type="time"> wherever the policy step / min / max must be
 * enforced in the UI (Chrome on macOS ignores those attributes on native
 * time inputs).
 */
export function TimeSelect({
  value,
  onChange,
  min = '09:00',
  max = '24:00',
  stepMinutes = 15,
  includeMax = false,
  placeholder = '—',
  ariaLabel,
  disabled = false,
}: Props) {
  const options = useMemo(() => {
    const minM = parseHm(min);
    const maxM = parseHm(max);
    const out: string[] = [];
    // minutesToHm is the shared 24-hour formatter. A total of 1440 renders as
    // "24:00" rather than wrapping to "00:00" — which is what makes the
    // includeMax boundary option below come out right.
    for (let t = minM; t < maxM; t += stepMinutes) {
      out.push(minutesToHm(t));
    }
    if (includeMax) {
      out.push(minutesToHm(maxM));
    }
    return out;
  }, [min, max, stepMinutes, includeMax]);

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      disabled={disabled}
      style={selectStyle}
    >
      <option value="">{placeholder}</option>
      {options.map((t) => (
        // value stays the canonical "HH:MM" (what onChange/the API expect);
        // the label honours the device's 12-/24-hour preference.
        <option key={t} value={t}>
          {formatClock(t)}
        </option>
      ))}
    </select>
  );
}

const selectStyle: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${PS.border}`,
  borderRadius: 3,
  minHeight: 40,
  background: PS.white,
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  color: PS.nearBlack,
};
